import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Tooltip,
  Modal
} from 'antd';
import {
  EditFilled,
  DeleteFilled,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeFilled
} from '@ant-design/icons';
import BatteryGauge from 'react-battery-gauge';
import { apiGetDevicesInformation } from '@services/ManageDevices/DeviceServices';
import { showError, showWarning } from '@components/notification';
import { communityList, downloadBlobFile } from '@utils/utils';
import StatCards from './StatCards';
import { useNavigate } from 'react-router-dom';
import ModalImportFile from './ModalImportFile';
import { UploadFileOutlined } from '@mui/icons-material';
import { URL_API } from '@configs/index';
import moment from 'moment';
import { apiDeleteCustomer, apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { loadFromLocalStorageObjectFromBase64 } from '@databases/localStorage';
import { contantAuthentication } from '@contants/index';
import { ADMIN_ROLE } from '@utils/constant';
import { useRole } from '@contexts/Auth/UseRole';

const { Option } = Select;
const { Title, Paragraph } = Typography;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('ALL');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));
  const { isWebAdmin, isWebManager, isWebSuperAdmin } = useRole();
  const [isFetching, setIsFetching] = useState(false);
  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetAllCustomer({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined
        // community: selectedCommunity === 'ALL' ? undefined : selectedCommunity
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  const handleEdit = (record) => {
    navigate(`/customer/${record.id}`);
  };

  const handleViewDetail = (record) => {
    navigate(`/customer/detail/${record.id}`);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá Khách hàng',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await apiDeleteCustomer(record?.id);
        fetchData();
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (!search) {
  //       setCurrentPage(1);
  //       fetchData();
  //     }
  //   }, 10000);

  //   return () => clearInterval(interval);
  // }, [search, selectedCommunity]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 80,
        fixed: 'left'
      },
      {
        title: 'DBĐD',
        dataIndex: 'code',
        fixed: 'left',
        width: 100
      },
      {
        title: 'Tên KH',
        dataIndex: 'name',
        fixed: 'left',
        width: 200
      },
      {
        title: 'CN',
        dataIndex: 'branch',
        width: 100
      },
      {
        title: 'TQL',
        dataIndex: 'manage_group',
        width: 100
      },
      {
        title: 'ĐHK',
        dataIndex: 'parent_device',
        width: 100
      },
      {
        title: 'Tuyến',
        dataIndex: 'route',
        width: 100
      },

      {
        title: 'Địa chỉ',
        dataIndex: 'address',
        width: 200
      },
      {
        title: 'Số Seri',
        dataIndex: 'serial_number',
        width: 100
      },
      {
        title: 'Số ĐT',
        dataIndex: 'phone',
        width: 100
      },
      {
        title: 'Tên ĐH',
        dataIndex: 'device_name',
        width: 100
      },
      {
        title: 'Ngày lắp',
        dataIndex: 'install_time',
        width: 100
      },
      {
        title: 'Cỡ ĐH',
        dataIndex: 'size',
        width: 100
      },
      {
        title: 'Loại ĐH',
        dataIndex: 'type',
        width: 100
      },
      {
        title: 'Kiểu ĐH',
        dataIndex: 'style',
        width: 100
      },
      {
        title: 'Ngày kiểm định',
        dataIndex: 'inspection_date',
        width: 150
      },
      {
        title: 'Kinh độ',
        dataIndex: 'longitude',
        width: 100
      },
      {
        title: 'Vĩ độ',
        dataIndex: 'latitude',
        width: 100
      },
      {
        title: 'Thời điểm ĐK',
        dataIndex: 'created_time',
        render: (text, record) => {
          return <>{moment(record?.created_time)?.format('DD/MM/YYYY HH:mm')}</>;
        },
        width: 140
      },
      {
        title: 'Ghi chú',
        dataIndex: 'note',
        width: 150
      },

      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                shape="circle"
                icon={<EyeFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {(isWebAdmin || isWebSuperAdmin) && (
              <>
                <Tooltip title="Chỉnh sửa">
                  <Button
                    type="text"
                    shape="circle"
                    icon={<EditFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleEdit(record)}
                  />
                </Tooltip>

                <Tooltip title="Xoá khách hàng">
                  <Button
                    type="danger"
                    shape="circle"
                    icon={<DeleteFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleDelete(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        )
      }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const handleResetSearch = () => {
    setSearch('');
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/customers/export-report`,
        'Danh sách khách hàng.xlsx',
        null,
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = () => {
    if (user?.user?.role !== ADMIN_ROLE) {
      showWarning({ message: 'Xin lỗi, bạn không có quyền thực hiện chức năng này' });
      return;
    }
    navigate('/customer/add');
  };

  return (
    <div className="dashboard">
      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchData();
        }}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ color: '#0174CF' }}>
            Quản lý khách hàng
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            {(isWebAdmin || isWebSuperAdmin) && (
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddDevice}
                style={{
                  color: '#0174CF',
                  height: '36px',
                  backgroundColor: '#E8EBEF',
                  borderColor: '#E8EBEF',
                  fontSize: '18px'
                }}>
                Thêm mới
              </Button>
            )}
            {(isWebAdmin || isWebSuperAdmin) && (
              <Button
                icon={<UploadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0174CF',
                  borderColor: '#0174CF',
                  fontSize: '18px'
                }}>
                Xuất file excel
              </Button>
            )}
            {(isWebAdmin || isWebSuperAdmin) && (
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  setVisibleModalImport(true);
                }}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0174CF',
                  borderColor: '#0174CF',
                  fontSize: '18px'
                }}>
                Nhập file excel
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Card>
        <Row gutter={[8, 8]}>
          {/* <Col md={6} xs={12}>
            <Select
              value={selectedCommunity}
              onChange={setSelectedCommunity}
              style={{ width: '100%', height: '36px' }}>
              {communityList.map((community) => (
                <Option key={community.value} value={community.value}>
                  {community.label}
                </Option>
              ))}
            </Select>
          </Col> */}
          <Col md={6} xs={12}>
            <Input
              value={search}
              onChange={handleSearchChange}
              placeholder="Tìm kiếm tên, mã kh..."
              style={{ width: '100%', marginBottom: '16px', height: '36px' }}
            />
          </Col>
          <Col span={12}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                color: '#fff',
                height: '36px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
        <Table
          bordered
          // size="middle"
          loading={isFetching}
          dataSource={deviceList || []}
          columns={columns}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: true,
            onChange: handleChangePage,
            onShowSizeChange: handleChangePageSize,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
          }}
          style={{
            borderRadius: '4px',
            thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
            fontSize: '30px'
          }}
          scroll={{ x: 'max-content', y: 600 }}
        />
      </Card>
    </div>
  );
}
